
import { useEffect, useState, useRef } from "react";
import { useAuthState } from "./auth/useAuthState";
import { supabase } from "@/integrations/supabase/client";
import { secureLog } from "@/utils/secureLogger";
import { isWhitelistedAdmin } from "@/utils/adminWhitelist";

export function useSupabaseAuth(skipAdminCheck = false) {
  const { user, session, isLoading: authLoading } = useAuthState();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isAdminLoading, setIsAdminLoading] = useState(!skipAdminCheck);
  const [adminError, setAdminError] = useState<string | null>(null);
  const hasCheckedAdminRef = useRef(false);
  const currentUserIdRef = useRef<string | null>(null);
  
  useEffect(() => {
    console.log("=== useSupabaseAuth useEffect START ===");
    console.log("useSupabaseAuth - User state changed:", { 
      hasUser: !!user, 
      userEmail: user?.email,
      userId: user?.id,
      authLoading,
      skipAdminCheck,
      hasCheckedAdmin: hasCheckedAdminRef.current,
      currentUserId: currentUserIdRef.current
    });

    // If user changed, reset admin check
    if (currentUserIdRef.current !== (user?.id || null)) {
      console.log("User changed, resetting admin state");
      hasCheckedAdminRef.current = false;
      currentUserIdRef.current = user?.id || null;
      setIsAdmin(false);
      setAdminError(null);
    }

    // Reset admin state when user logs out
    if (!user) {
      console.log("No user - resetting admin state");
      setIsAdmin(false);
      setIsAdminLoading(false);
      setAdminError(null);
      hasCheckedAdminRef.current = false;
      currentUserIdRef.current = null;
      return;
    }

    // Skip if admin check is disabled
    if (skipAdminCheck) {
      console.log("Skipping admin check (disabled)");
      setIsAdminLoading(false);
      setIsAdmin(false);
      return;
    }

    // Skip if still loading auth or already checked for this user
    if (authLoading || hasCheckedAdminRef.current) {
      console.log("Skipping admin check:", { 
        authLoading, 
        alreadyChecked: hasCheckedAdminRef.current 
      });
      return;
    }

    const checkAdminStatus = async () => {
      console.log("=== ADMIN CHECK STARTING ===");
      console.log("Starting admin check for user:", user.email);
      setIsAdminLoading(true);
      setAdminError(null);
      
      try {
        // Check special admin emails first (for immediate access)
        if (isWhitelistedAdmin(user.email)) {
          console.log("=== SPECIAL ADMIN USER DETECTED ===");
          console.log("Special admin user detected:", user.email);
          secureLog.debug("Special admin user detected", { email: user.email });
          setIsAdmin(true);
          hasCheckedAdminRef.current = true;
          setIsAdminLoading(false);
          console.log("Admin check completed - SPECIAL USER");
          return;
        }

        // Check database admin status
        console.log("Checking database admin status via RPC");
        const { data, error } = await supabase.rpc('is_admin');
        
        console.log("=== RPC is_admin RESULT ===", { data, error: error?.message });
        
        if (error) {
          console.error("Admin RPC check failed:", error);
          secureLog.error("Admin check failed", error);
          setAdminError(error.message);
          setIsAdmin(false);
        } else {
          const adminStatus = !!data;
          console.log("=== ADMIN CHECK COMPLETED ===");
          console.log("RPC result - isAdmin:", adminStatus, "rawData:", data);
          secureLog.debug("Admin check completed", { isAdmin: adminStatus });
          setIsAdmin(adminStatus);
        }
        
        hasCheckedAdminRef.current = true;
      } catch (err) {
        console.error("=== ADMIN CHECK EXCEPTION ===", err);
        secureLog.error("Admin check exception", err);
        setAdminError(`Exception: ${(err as Error).message}`);
        setIsAdmin(false);
        hasCheckedAdminRef.current = true; // Mark as checked to prevent retry loops
      } finally {
        console.log("Admin check finally block - setting loading false");
        setIsAdminLoading(false);
      }
    };
    
    checkAdminStatus();
  }, [user, authLoading, skipAdminCheck]);
  
  // Calculate overall loading state
  const isLoading = authLoading || isAdminLoading;
  
  
  return { 
    user, 
    session, 
    isAdmin, 
    isLoading,
    authLoading,
    adminError
  };
}
